import { Box } from '@stenajs-webui/core';
import { Banner, stenaBusinessCard, stenaHazardous } from '@stenajs-webui/elements';
import type { PropsWithChildren } from 'react';
import { animalDropdownNoOption } from '../../../../../common/graphql/useAnimalTypesOptions';
import { NoValidCustomersBanner } from '../../../../../common/header-banners/NoValidCustomersBanner';
import { routeCreators } from '../../../../../common/routes/AppRoutes';
import { Information } from '../../../../../common/texts';
import { useUpdateField } from '../../../../../common/update-information/hooks/useUpdateField';
import { resolveInformationMessageBannerVariant } from '../../../../../common-ui/information-message/informationMessageUtils';
import { FlatButtonLink } from '../../../../../common-ui/Link';
import type { BasicBookingFragment } from '../../../../../gql/graphql';
import type { BookingFormState } from '../../../../../services/update-form/updateFormReducer';
import { getHazardousGoodsApprovalInfo } from '../../utils/detailsUtils';
import { resolveMiscInformation } from '../../utils/resolveMiscInformation';
import { shouldShowHazGoodsPaymentInformation } from '../../utils/showPaymentUtil';

interface BookingDetailsEditBannersProps extends PropsWithChildren {
  booking: BasicBookingFragment;
  formState: BookingFormState;
}

export function BookingDetailsEditBanners({
  children,
  booking,
  formState,
}: BookingDetailsEditBannersProps) {
  const showHazGoodsPaymentInformation = shouldShowHazGoodsPaymentInformation(booking);
  const hasLivestock = formState.livestockType?.value !== animalDropdownNoOption.value;
  const hasHazardousGoods = Boolean(formState.hazardousGoods);
  const hasUnsavedHazGoods = hasHazardousGoods && !booking.hazardousGoods;
  const { editedValue: empty } = useUpdateField('empty');

  const hazardousGoodsApprovalInfo = getHazardousGoodsApprovalInfo(
    booking.hazardousGoods,
    booking.hazardousGoodsApproved,
    booking.bookingStatus.status,
  );

  const miscInformation = resolveMiscInformation(hasLivestock, hasUnsavedHazGoods);

  return (
    <Box width={'100%'} gap>
      <InitialGapHack />
      {hasHazardousGoods && hazardousGoodsApprovalInfo && (
        <Banner
          icon={stenaHazardous}
          variant={resolveInformationMessageBannerVariant(hazardousGoodsApprovalInfo.type)}
          text={hazardousGoodsApprovalInfo.descriptionText}
          contentRight={
            booking.hazardousGoodsApproved && (
              <FlatButtonLink
                to={routeCreators.hazardousGoods({ bookingNo: booking.bookingNo })}
                label={'See details'}
              />
            )
          }
        />
      )}
      {showHazGoodsPaymentInformation && (
        <Banner
          icon={stenaBusinessCard}
          variant={'info'}
          text={Information.HazardousGoodsConfirmedPayableOnline}
        />
      )}
      {children}
      <NoValidCustomersBanner />
      {miscInformation && !empty && (
        <Banner variant={'warning'} text={miscInformation.text} icon={miscInformation.icon} />
      )}
    </Box>
  );
}

const InitialGapHack = () => <div />;
